
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Home,
  BarChart3,
  Briefcase,
  Search,
  Brain,
  Wrench,
  LogOut,
  FileText,
  FolderDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  marketingBusinessItems,
  seoItems,
  productivityItems,
  utilitiesItems,
  accountItems,
  adminMenuItems,
} from "@/components/sidebar/menu-items";
import { useAuth } from "@/hooks/useAuth";
import { isAdminEmail } from "@/constants/admin";
import { AdZoneSidebar } from "@/features/ad-zones/components/AdZonePlacements";
import { useSidebar } from "@/components/ui/sidebar";
import { MenuItem } from "./types/menu-item";

const categories = [
  { id: "home", label: "Accueil", icon: Home, items: accountItems, url: "/welcome" },
  { id: "dashboard", label: "Tableau de bord", icon: BarChart3, url: "/dashboard", requiresAuth: true },
  { id: "reports", label: "Mes rapports", icon: FileText, url: "/mes-rapports", requiresAuth: true },
  { id: "exports", label: "Mes Exports", icon: FolderDown, url: "/mes-exports" },
  { id: "marketing", label: "Marketing & Business", icon: Briefcase, items: marketingBusinessItems },
  { id: "seo", label: "SEO & Visibilité", icon: Search, items: seoItems },
  { id: "productivity", label: "Productivité", icon: Brain, items: productivityItems },
  { id: "utilities", label: "Utilitaires", icon: Wrench, items: utilitiesItems },
];

function getCategoryItems(activeCategory: string): MenuItem[] {
  if (activeCategory === "admin") return adminMenuItems;
  return categories.find((c) => c.id === activeCategory)?.items || [];
}

function getCategoryTitle(activeCategory: string): string {
  if (activeCategory === "admin") return "ADMINISTRATION";
  return categories.find((c) => c.id === activeCategory)?.label || "";
}

export function SidebarMain() {
  const { signOut, user } = useAuth();
  const { isMobile, setOpenMobile } = useSidebar();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);

  const showAdminItems = isAdminEmail(user?.email);

  const closeDrawer = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleCategoryClick = (id: string, url?: string, requiresAuth?: boolean) => {
    if (url) {
      if (requiresAuth && !user) return;
      setActiveCategory(null);
      closeDrawer();
      return;
    }

    setActiveCategory((prev) => (prev === id ? null : id));
  };

  if (activeCategory) {
    const items = getCategoryItems(activeCategory);
    const title = getCategoryTitle(activeCategory);

    return (
      <div className="flex flex-col h-full min-h-0 bg-[#23486a] text-white">
        <button
          type="button"
          className="flex items-center gap-2 px-4 py-3 border-b border-yellow-300 bg-yellow-200 text-black text-left hover:bg-yellow-300 shrink-0"
          onClick={() => setActiveCategory(null)}
        >
          <ChevronLeft className="w-5 h-5 shrink-0" />
          <span className="font-medium text-sm truncate">{title}</span>
        </button>
        <nav className="flex-1 overflow-y-auto py-2">
          {items.map((item) => (
            <Link
              key={item.id}
              to={item.url}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-sm hover:bg-[#1e3a57] transition",
                !item.available && "opacity-50 pointer-events-none"
              )}
              onClick={() => {
                setActiveCategory(null);
                closeDrawer();
              }}
            >
              {item.icon && <item.icon className="w-4 h-4 shrink-0 text-[#EFB036]" />}
              <span className="truncate">{item.title}</span>
            </Link>
          ))}
        </nav>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0">
      <div className="w-full lg:w-[var(--sidebar-w)] bg-[#23486a] text-white flex flex-col py-2 sm:py-4 h-full min-h-0 overflow-y-auto">
        {categories.map((cat) => {
          const requiresAuth = cat.requiresAuth;
          const isDisabled = requiresAuth && !user;
          const hasSubmenu = !cat.url;

          if (hasSubmenu) {
            return (
              <button
                key={cat.id}
                type="button"
                className={cn(
                  "flex items-center gap-3 px-4 py-3 text-left hover:bg-[#1e3a57] transition w-full",
                  activeCategory === cat.id && "bg-[#1a324d]"
                )}
                onClick={() => handleCategoryClick(cat.id, cat.url, cat.requiresAuth)}
              >
                <cat.icon className="w-5 h-5 shrink-0" />
                <span className="font-medium text-sm flex-1 text-left">{cat.label}</span>
                <ChevronRight className="w-4 h-4 shrink-0 opacity-60" />
              </button>
            );
          }

          return (
            <Link
              key={cat.id}
              to={isDisabled ? "/auth" : cat.url!}
              className={cn(
                "flex items-center gap-3 px-4 py-3 text-left hover:bg-[#1e3a57] transition",
                isDisabled && "opacity-90"
              )}
              onClick={() => {
                if (!isDisabled) closeDrawer();
              }}
            >
              <cat.icon className="w-5 h-5 shrink-0" />
              <span className="font-medium text-sm">{cat.label}</span>
              {requiresAuth && !user && (
                <span className="ml-auto text-[10px] leading-tight text-yellow-400 font-medium">
                  connexion requise
                </span>
              )}
            </Link>
          );
        })}

        {showAdminItems && (
          <button
            type="button"
            className={cn(
              "flex items-center gap-3 px-4 py-3 text-left hover:bg-[#1e3a57] transition w-full",
              activeCategory === "admin" && "bg-[#1a324d]"
            )}
            onClick={() => handleCategoryClick("admin")}
          >
            <Wrench className="w-5 h-5 shrink-0" />
            <span className="font-medium text-sm flex-1 text-left">ADMINISTRATION</span>
            <ChevronRight className="w-4 h-4 shrink-0 opacity-60" />
          </button>
        )}

        <AdZoneSidebar className="mt-10 pt-4" />

        {user && (
          <div className="mt-auto pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={() => {
                signOut();
                closeDrawer();
              }}
              className="flex items-center gap-3 px-4 py-3 text-left hover:bg-[#1e3a57] transition w-full text-red-400"
            >
              <LogOut className="w-5 h-5 shrink-0" />
              <span className="font-medium text-sm">Déconnexion</span>
            </button>
          </div>
        )}
      </div>

    </div>
  );
}
